import React, { ReactElement, useCallback, useEffect, useState } from 'react';
import './EditPricingVersionDetails.scss';
import { useHistory, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import ActionableHeaderCard from 'ps-components/Card/ActionableHeaderCard';
import {
  Button,
  Checkbox,
  DefaultModalDialog,
  Icon,
  Modal,
  MultiAutocomplete,
  Radio,
  RadioGroup,
  useDisclosure
} from '../../../../ps-aspect-design';
import InlineEdit from 'ps-components/InlineEdit/InlineEdit';
import { ProgressStepper, Step } from 'ps-components/ProgressStepper/ProgressStepper';
import { useQueryClient } from 'react-query';
import addAlert from 'ps-components/AlertToast/addAlert';
import {
  APPLY_VERSION_CHOICES,
  createRule,
  deleteRule,
  downloadUploadErrorsToExcel,
  downloadVersion,
  generateReport,
  getMarketPricingElements,
  getRulesBasedPricingRules,
  getUniqueJobCodeKeys,
  getUploadErrors,
  getVersions,
  MarketElements,
  promoteVersion,
  RuleBasedPricingRule,
  runTheRules,
  updateRule,
  updateVersion,
  useRules,
  useVersion
} from '../GlobalJobMatchingService';
import { shortDateString } from 'ps-components/lib/formatters';
import { MatchStrategy, PricingVersion, PromoteMode, PropagateMatchOption, RunStatus } from '../types/versions';
import isEqual from 'lodash/isEqual';
import RuleStepCard from './RuleStepCard';
import { PricingRule, RuleStep } from '../types/rules';
import { CreatePricingRulesStep, DefineSurveyJobMatchesStep } from './ProgressStepperSteps';
import EditPricingVersionDetailsUploadModal from './EditPricingVersionDetailsUploadModal';
import { DATA_SOURCE, useElements } from 'ps-components/FieldFilters/FieldFilterService';
import Select from 'ps-components/ReactSelect/ReactSelect';
import Dropdown from 'ps-components/Dropdown/Dropdown';
import ApplyToJobsDropdown from './ApplyToJobsDropdown';
import CheckboxInput from 'ps-components/Input/CheckboxInput';

interface UrlParams {
  id: string;
}

interface CurrentRuleSteps {
  [key: string]: RuleStep;
}

enum GENERATE_REPORT_OPTIONS {
  PRICING_VERSION_DETAILS = "Market Pricing Details by Job",
  PRICING_VERSION_SUMMARY = "Market Pricing Composite Summary by Job",
  PRICING_VERSION_MATCH_PRIORITIES = "Matches Download",
  PRICING_VERSION_STATS = "Version Statistics"
}

async function deleteRuleWithCallback(id: number, masterAccountId: string, cb: Function): Promise<void> {
  const deleteResult = await deleteRule(masterAccountId, id);
  if (cb) cb(deleteResult);
}

const emptyPricingVersion: PricingVersion = {
  id: 0,
  name: '',
  organizationId: 0,
  jobCount: 0,
  isCurrent: false,
  description: '',
  matchStrategy: MatchStrategy.CURRENT,
  created: '',
  createdBy: '',
  lastModified: '',
  lastModifiedBy: '',
  shouldPropagate: false,
  propagateMatchOption: PropagateMatchOption.ALL,
  propagateMatchJobSelection: [],
  requiresRun: true,
  hasNotified: false,
  runStatus: null
};

const VERSION_NAME_MINIMUM_LENGTH = 3;
const VERSION_NAME_MAXIMUM_LENGTH = 50;

// TODO: Move these to 'hooks' directory and type them out
const getMasterAccountID = (state: any) => state.session?.organization?.masterAccountId;
const getDefaultGroup = (state: any) => state.session?.organization?.defaultGroup;
const getGroups = (state: any) => state.groups;
const getCSRFPair = (state: any) => state.session?.csrfPair;
const getUsePropagate = (state: any) => {
  const flags: {name: string; enabled: boolean}[] = state.session.featureFlags;
  const propagateFlag = flags.find(flag => flag.name === 'usePropagateMatches');
  return propagateFlag?.enabled === true;
}
const getUseJobCodeKey = (state: any) => state.session.organization.useJobCodeKey === true;

function EditPricingVersionDetails({
  launchDarklyFeatureFlags
}: {
  launchDarklyFeatureFlags: { [key: string]: boolean };
}): ReactElement {
  const history = useHistory();
  const { id } = useParams<UrlParams>();
  const defaultGroup = useSelector(getDefaultGroup);
  const groups = useSelector(getGroups);
  const csrfPair = useSelector(getCSRFPair);
  const usePropagateMatches = useSelector(getUsePropagate);
  const useJobCodeKey = useSelector(getUseJobCodeKey);
  const [jobPricingRules, setJobPricingRules] = useState<RuleBasedPricingRule[]>([]);
  const [marketElements, setMarketElements] = useState<MarketElements>({ payElements: [] });
  const masterAccountId = useSelector(getMasterAccountID);
  const { key, data, error, isFetching: versionIsFetching, isFetched: isVersionFetched } = useVersion(masterAccountId, id);
  const { key: rulesKey, data: rules, error: rulesError, isFetching: rulesIsFetching } = useRules(masterAccountId, id);
  const { data: filters, isFetched: areFiltersLoaded, isFetching: areFiltersLoading } = useElements(
    masterAccountId,
    'jobs',
    [DATA_SOURCE.JOBS]
  );
  const isFetching = versionIsFetching || rulesIsFetching || areFiltersLoading;

  const queryClient = useQueryClient();

  const [currentRuleSteps, setCurrentRuleSteps] = useState<CurrentRuleSteps>({});
  const [pricingVersionDetails, setPricingVersionDetails] = useState<PricingVersion>(emptyPricingVersion);
  const [pricingVersionRules, setPricingVersionRules] = useState<PricingRule[]>([]);
  const [jobCodeKeyOptions, setJobCodeKeyOptions] = useState<string[]>([]);
  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [editDescriptionValue, setEditDescriptionValue] = useState('');
  const { isOpen: isUploadModalOpen, onOpen: onUploadModalOpen, onClose: onUploadModalClose } = useDisclosure();
  const { isOpen: isRuleDeleteOpen, onOpen: onRuleDeleteOpen, onClose: onRuleDeleteClose } = useDisclosure();
  const { isOpen: isApplyVersionOpen, onOpen: onApplyVersionOpen, onClose: onApplyVersionClose } = useDisclosure();
  const { isOpen: isGenerateReportModalOpen, onOpen: onGenerateReportOpen, onClose: onGenerateReportClose } = useDisclosure();
  const [selectedRule, setSelectedRule] = useState<PricingRule>();
  const [applyVersionChoice, setApplyVersionChoice] = useState<APPLY_VERSION_CHOICES>();
  const [generateReportOption, setGenerateReportOption] = useState<GENERATE_REPORT_OPTIONS>();
  const [isRunTheRulesLoading, setIsRunTheRulesLoading] = useState<boolean>(false);
  const [versions, setVersions] = useState<PricingVersion[]>([]);
  const [selectedVersionIds, setSelectedVersionIds] = useState<number[]>([]);
  const [isGeneratingReport, setIsGeneratingReport] = useState<boolean>(false);
  const [isApplyingVersion, setIsApplyingVersion] = useState<boolean>(false);
  const [versionPollingInterval, setVersionPollingInterval] = useState<number>();
  const [includeOfficialPricing, setIncludeOfficialPricing] = useState<boolean>(false);

  const handleVersionUpdate = useCallback(
    (newDetails: PricingVersion, successMessage?: string, errorMessage?: string): boolean => {
      if (!isEqual(pricingVersionDetails, newDetails)) {
        if (
          pricingVersionDetails.name !== newDetails.name &&
          (newDetails.name.length < VERSION_NAME_MINIMUM_LENGTH || newDetails.name.length > VERSION_NAME_MAXIMUM_LENGTH)
        ) {
          return false;
        }
        updateVersion(masterAccountId, id, newDetails)
          .then(() => {
            queryClient.invalidateQueries(key);
            if (successMessage) addAlert(successMessage, 'success', 5000);
          })
          .catch(e => {
            let hideContactSupport = false;
            if (e.response.data === 'Conflict') {
              errorMessage = `Version with name "${newDetails.name}" already exists`;
              hideContactSupport = true;
            }
            addAlert(errorMessage ?? 'Version failed to update.', 'danger', 5000, 'top-center', hideContactSupport);
          });
        return true;
      }
      return false;
    },
    [pricingVersionDetails, editDescriptionValue, queryClient, masterAccountId, id]
  );

  const startPollingVersion = () => {
    const versionPollingInterval = window.setInterval(() => {
      queryClient.invalidateQueries(key);
    }, 5000);
    setVersionPollingInterval(versionPollingInterval);
  }

  useEffect(() => {
    getRulesBasedPricingRules().then(setJobPricingRules);
    getMarketPricingElements().then(setMarketElements);
    localStorage.setItem("fromGlobalJobMatching", "true");
    document.addEventListener("visibilitychange", reFetchRules);


    return () => {
      window.clearInterval(versionPollingInterval);
      setVersionPollingInterval(undefined);
      localStorage.removeItem("fromGlobalJobMatching");
      document.removeEventListener("visibilitychange", reFetchRules);
    };
  }, []);

  useEffect(() => {
    if (data) {
      const details = data.data;
      setPricingVersionDetails(details);
      if (details?.description !== editDescriptionValue) {
        setEditDescriptionValue(details.description);
      }
      if (details.runStatus === RunStatus.SUCCESS || details.runStatus === RunStatus.FAILED) {
        window.clearInterval(versionPollingInterval);
        setVersionPollingInterval(undefined);
        if (!details.hasNotified) {
          if (details.runStatus === RunStatus.SUCCESS) {
            addAlert('Run the rules has completed successfully', 'success', 5000);
          } else if (details.runStatus === RunStatus.FAILED) {
            addAlert('Run the rules could not complete successfully', 'error', 5000);
          }

          const newDetails = {
            ...details,
            hasNotified: true
          };
          handleVersionUpdate(newDetails);
        }
      } else if (details.runStatus === RunStatus.RUNNING && versionPollingInterval === undefined) {
        startPollingVersion();
      }
    }
  }, [data]);

  useEffect(() => {
    if (isVersionFetched && pricingVersionDetails.matchStrategy === MatchStrategy.FILE && pricingVersionDetails.shouldPropagate) {
      getUniqueJobCodeKeys(pricingVersionDetails.id).then((data) => setJobCodeKeyOptions(data.jobCodeKeys));
    }
  }, [pricingVersionDetails])

  useEffect(() => {
    if (rules?.data) {
      setPricingVersionRules([...rules.data]);
      const ruleSteps = { ...currentRuleSteps };
      rules.data.forEach(rule => {
        const ruleStep = ruleSteps[rule.id];
        if (!ruleStep) {
          ruleSteps[rule.id] = RuleStep.SELECT_JOBS;
        }
      });
      setCurrentRuleSteps(ruleSteps);
    }
  }, [rules]);

  const reFetchRules = () => {
    if (!document.hidden) {
      getRulesBasedPricingRules().then(setJobPricingRules);
    }
  }

  const changeDescription = (event: React.ChangeEvent<HTMLTextAreaElement>): void => {
    if (editDescriptionValue !== event.target.value) {
      setEditDescriptionValue(event.target.value);
    }
  };

  const onUploadModalSuccess = async () => {
    const uniqueCodeKeys = await getUniqueJobCodeKeys(pricingVersionDetails.id);
    setJobCodeKeyOptions(uniqueCodeKeys.jobCodeKeys);
    queryClient.invalidateQueries(key);
  }

  const handleRuleUpdate = useCallback(
    (newRuleDetails: PricingRule, successMessage?: string, errorMessage?: string): boolean => {
      const existingRule = pricingVersionRules.find(rule => rule.id === newRuleDetails.id);
      if (existingRule && !isEqual(existingRule, newRuleDetails)) {
        if (
          existingRule.name !== newRuleDetails.name &&
          (newRuleDetails.name.length < VERSION_NAME_MINIMUM_LENGTH || newRuleDetails.name.length > VERSION_NAME_MAXIMUM_LENGTH)
        ) {
          return false;
        }
        updateRule(masterAccountId, newRuleDetails.id, newRuleDetails)
          .then(async () => {
            await queryClient.invalidateQueries(rulesKey);
            await queryClient.invalidateQueries(key);
            if (successMessage) addAlert(successMessage, 'success', 5000);
          })
          .catch(() => {
            addAlert(errorMessage, 'danger', 5000);
          });
        return true;
      }
      return false;
    },
    [pricingVersionRules, queryClient, masterAccountId, id]
  );

  const handleDescriptionUpdate = (): boolean => {
    const newDetails = {
      ...pricingVersionDetails,
      description: editDescriptionValue
    };
    const successMessage = 'Description successfully updated';
    const errorMessage = 'Could not update description';
    return handleVersionUpdate(newDetails, successMessage, errorMessage);
  };

  const handleMatchStrategyUpdate = (newValue: MatchStrategy): boolean => {
    const newDetails = {
      ...pricingVersionDetails,
      matchStrategy: newValue
    };
    const errorMessage = 'Could not update match strategy';
    return handleVersionUpdate(newDetails, undefined, errorMessage);
  };

  const onDescriptionKey = useCallback(
    (event: React.KeyboardEvent<HTMLTextAreaElement>): void => {
      if (event.key === 'Enter' && !event.shiftKey) {
        setIsEditingDescription(false);
        handleDescriptionUpdate();
      } else if (event.key === 'Escape') {
        setIsEditingDescription(false);
        setEditDescriptionValue(pricingVersionDetails.description);
      }
    },
    [setIsEditingDescription, handleDescriptionUpdate, setEditDescriptionValue, pricingVersionDetails]
  );

  const renderDescription = (): ReactElement => {
    return (
      <div className="pricing-version__description-container">
        {!isEditingDescription ? (
          <div className="edit-description__link" onClick={(): void => setIsEditingDescription(true)}>
            {pricingVersionDetails.description
              ? pricingVersionDetails.description.length > 100
                ? pricingVersionDetails.description.slice(0, 100) + '...'
                : pricingVersionDetails.description
              : 'Add Description'}
          </div>
        ) : (
          <>
            <textarea
              id="pricing-version-description-textarea"
              className="pricing-version-description__textarea"
              onChange={changeDescription}
              onKeyDown={onDescriptionKey}
              onBlur={(): void => {
                setIsEditingDescription(false);
                handleDescriptionUpdate();
              }}
              autoFocus={isEditingDescription}
              value={editDescriptionValue}
            />
            <div className="pricing-version-description__instruction-text">
              <span className="bold">Enter</span>
              <span className="text">save</span>
              <span className="bold">Esc</span>
              <span className="text">cancel</span>
            </div>
          </>
        )}
      </div>
    );
  };

  const retrieveProgressSteps = (): typeof Step[] => {
    return [
      {
        title: 'Define Survey Job Matches',
        description: 'Define for all jobs in this version',
        StepContent: DefineSurveyJobMatchesStep(pricingVersionDetails)
      },
      {
        title: 'Select jobs to include in version',
        StepContent: CreatePricingRulesStep({ pricingVersionRules, groups, jobPricingRules, marketElements })
      }
    ];
  };

  const goToNextRuleStep = (rule: PricingRule): void => {
    const newRuleSteps = { ...currentRuleSteps };
    if (currentRuleSteps[rule.id] === RuleStep.SELECT_JOBS) {
      newRuleSteps[rule.id] = RuleStep.DATA_CUT_PRIORITIES;
    } else if (currentRuleSteps[rule.id] === RuleStep.DATA_CUT_PRIORITIES) {
      newRuleSteps[rule.id] = RuleStep.METHODOLOGY;
    }
    setCurrentRuleSteps(newRuleSteps);
  };

  const goToPreviousRuleStep = (rule: PricingRule): void => {
    const newRuleSteps = { ...currentRuleSteps };
    if (currentRuleSteps[rule.id] === RuleStep.DATA_CUT_PRIORITIES) {
      newRuleSteps[rule.id] = RuleStep.SELECT_JOBS;
    } else if (currentRuleSteps[rule.id] === RuleStep.METHODOLOGY) {
      newRuleSteps[rule.id] = RuleStep.DATA_CUT_PRIORITIES;
    }
    setCurrentRuleSteps(newRuleSteps);
  };

  const backToLanding = useCallback((): void => history.push('/market-analysis/global-job-matching'), []);

  const createNewRule = useCallback(async () => {
    const newRule = { name: 'Jobs', globalJobPricingVersionId: parseInt(id) } as PricingRule;
    await createRule(masterAccountId, newRule).then(async () => {
      await queryClient.invalidateQueries(rulesKey);
      await queryClient.invalidateQueries(key);
    });
  }, [masterAccountId, id]);

  const handleDeleteRule = useCallback(
    (rule: PricingRule) => async () => {
      setSelectedRule(rule);
      onRuleDeleteOpen();
    },
    [setSelectedRule, onRuleDeleteOpen]
  );

  const deleteSelectedRule = useCallback(async () => {
    if (selectedRule) {
      await deleteRuleWithCallback(selectedRule.id, masterAccountId, onRuleDeleteClose);
      await queryClient.invalidateQueries(rulesKey);
      await queryClient.invalidateQueries(key);
    }
  }, [selectedRule, masterAccountId, onRuleDeleteClose, queryClient]);

  const applyPricingVersion = useCallback(async () => {
    if (applyVersionChoice) {
      setIsApplyingVersion(true);
      if (applyVersionChoice === APPLY_VERSION_CHOICES.CREATE_NEW) {
        await promoteVersion(masterAccountId, pricingVersionDetails.id, PromoteMode.CREATE_NEW);
      } else if (applyVersionChoice === APPLY_VERSION_CHOICES.OVERWRITE_EXISTING) {
        await promoteVersion(masterAccountId, pricingVersionDetails.id, PromoteMode.OVERWRITE_EXISTING);
      } else if (applyVersionChoice === APPLY_VERSION_CHOICES.OVERWRITE_ALL_AND_PRESERVE) {
        await promoteVersion(masterAccountId, pricingVersionDetails.id, PromoteMode.OVERWRITE_ALL_AND_PRESERVE);
      }
      await queryClient.invalidateQueries(key);
      addAlert('Pricing version has been applied', 'success', 5000);
      setIsApplyingVersion(false);
      onApplyVersionClose();
    }
  }, [applyVersionChoice, masterAccountId, onApplyVersionClose, queryClient]);

  const generateVersionsReport = useCallback(async () => {
    if (generateReportOption) {
      setIsGeneratingReport(true);
      if (generateReportOption === GENERATE_REPORT_OPTIONS.PRICING_VERSION_DETAILS) {
        await generateReport('market_pricing_versions_matches', csrfPair, selectedVersionIds, includeOfficialPricing);
      } else if (generateReportOption === GENERATE_REPORT_OPTIONS.PRICING_VERSION_SUMMARY) {
        await generateReport('market_pricing_versions_summaries', csrfPair, selectedVersionIds, includeOfficialPricing);
      }
      setIsGeneratingReport(false);
      onGenerateReportClose();
    }
  }, [generateReportOption, masterAccountId, onGenerateReportClose, selectedVersionIds, includeOfficialPricing]);

  const renderRuleDeleteModal = (): ReactElement => {
    return (
      <Modal isOpen={isRuleDeleteOpen} onClose={onRuleDeleteClose} className="global-job-matching__delete-dialog">
        <DefaultModalDialog
          primaryText="Delete"
          secondaryText="Cancel"
          primaryCallback={deleteSelectedRule}
          secondaryCallback={onRuleDeleteClose}
          title="Confirm Delete"
          primaryDisabled={false}
          secondaryDisabled={false}
        >
          <span>You have selected to delete rule </span>
          <b>“{selectedRule?.name}”</b>
          <span>. Please confirm your choice, or cancel and return to the page.</span>
        </DefaultModalDialog>
      </Modal>
    );
  };

  const renderApplyVersionModalFooter = (): ReactElement => {
    return (
      <div className="report__footer">
        <Button variant="secondary" disabled={isApplyingVersion} onClick={onApplyVersionClose}>Cancel</Button>
        <Button variant="primary" disabled={isApplyingVersion} onClick={applyPricingVersion}>
          {isApplyingVersion && <Icon className="spinner" name="Cw" spin={true}/>}
          <span>Apply</span>
        </Button>
      </div>
    )
  }

  const renderApplyToJobsConfirmModal = (): ReactElement => {
    return (
      <Modal isOpen={isApplyVersionOpen} onClose={onApplyVersionClose} className="global-job-matching__apply-dialog">
        <DefaultModalDialog
          title="Confirm Apply"
          customFooter={renderApplyVersionModalFooter()}
        >
          <span>You have selected to </span>
          <b>“{applyVersionChoice}”</b>
          <span>. Please confirm your choice, or cancel and return to the page.</span>
        </DefaultModalDialog>
      </Modal>
    );
  }

  const getVersionSelections = (): string[] => {
    return versions.filter(version => selectedVersionIds.includes(version.id)).map(version => version.name);
  }

  const getAutocompleteSuggestions = (): string[] => {
    return versions.filter(version => !selectedVersionIds.includes(version.id)).map(version => version.name);
  }

  const renderReportModalFooter = (): ReactElement => {
    return (
      <div className="report__footer">
        <Button variant="secondary" disabled={isGeneratingReport} onClick={onGenerateReportClose}>Cancel</Button>
        <Button variant="primary" disabled={isGeneratingReport} onClick={generateVersionsReport}>
          {isGeneratingReport && <Icon className="spinner" name="Cw" spin={true}/>}
          <span>Generate Report</span>
        </Button>
      </div>
    )
  }

  const renderGenerateReportModal = (): ReactElement => {
    return (
      <Modal isOpen={isGenerateReportModalOpen} onClose={onGenerateReportClose} className="global-job-matching__generate-report-dialog">
        <DefaultModalDialog
          title="Select Pricing Versions"
          customFooter={renderReportModalFooter()}
        >
          <span>Select up to five pricing versions to include in the report.</span>
          <MultiAutocomplete
            className="pricing-versions__multiselect"
            forceSelect={true}
            onClear={() => {
              setSelectedVersionIds([]);
            }}
            onSelectionChange={values => {
              const versionIds = versions.filter(version => values.includes(version.name)).map(version => version.id);
              setSelectedVersionIds(versionIds);
            }}
            selections={getVersionSelections()}
            suggestions={getAutocompleteSuggestions()}
            maxSelections={5}
          />
          <div className="include-official-pricing">
            <CheckboxInput
              checked={includeOfficialPricing}
              onClick={() => {
                const newValue = !includeOfficialPricing;
                setIncludeOfficialPricing(newValue);
              }}
            />
            Include Official Pricing
          </div>
        </DefaultModalDialog>
      </Modal>
    );
  }

  const getJobCodeKeyOptions = () => {
    return jobCodeKeyOptions.map(jck => { return { value: jck, label: jck } });
  }

  const getSelectedJobCodeKeyOptions = () => {
    return pricingVersionDetails?.propagateMatchJobSelection?.map(jck => { return { value: jck, label: jck } }) || [];
  }

  const requiresMatchFileUpload = () => {
    if (pricingVersionDetails?.matchStrategy === MatchStrategy.FILE) {
      return !pricingVersionDetails?.metadata?.filename;
    }
    return false;
  }

  const renderPropagate = () => {
    return (
      <>
        {usePropagateMatches && useJobCodeKey &&
        <>
          <Checkbox
            label="Propagate Upload Match Priorities"
            checked={pricingVersionDetails.shouldPropagate}
            onChange={(e, checked) => {
              const shouldPropagate = !!checked;
              const newDetails = { ...pricingVersionDetails, shouldPropagate };
              return handleVersionUpdate(newDetails);
            }}
          />
          {pricingVersionDetails.shouldPropagate &&
          <div className="propagate-options">
            <span className="propagate__subtext">
              Use the counterpart matches for jobs with the same job code but different job code key.
            </span>
            <RadioGroup name={`priority-${pricingVersionDetails.id}`}>
              <Radio
                label="Propagate to all other jobs"
                value={PropagateMatchOption.ALL}
                checked={pricingVersionDetails.propagateMatchOption === PropagateMatchOption.ALL}
                onChange={(): void => {
                  const newDetails = {
                    ...pricingVersionDetails,
                    propagateMatchOption: PropagateMatchOption.ALL
                  };
                  handleVersionUpdate(newDetails);
                }}
              />
              <Radio
                label="To selected jobs"
                value={PropagateMatchOption.SELECT_JOBS}
                checked={pricingVersionDetails.propagateMatchOption === PropagateMatchOption.SELECT_JOBS}
                onChange={(): void => {
                  const newDetails = {
                    ...pricingVersionDetails,
                    propagateMatchOption: PropagateMatchOption.SELECT_JOBS
                  };
                  handleVersionUpdate(newDetails);
                }}
              />
            </RadioGroup>
            {pricingVersionDetails.propagateMatchOption === PropagateMatchOption.SELECT_JOBS &&
            <Select
              className="propagate-match__select"
              isClearable={true}
              isMulti
              onChange={(selectedJobCodeKeyOptions: { value: string }[]) => {
                const selectedJobCodeKeys = selectedJobCodeKeyOptions.map(option => option.value);
                const newDetails = {
                  ...pricingVersionDetails,
                  propagateMatchJobSelection: selectedJobCodeKeys
                };
                handleVersionUpdate(newDetails);
              }}
              noOptionsMessage={() => 'No results found'}
              options={getJobCodeKeyOptions()}
              placeholder="Type to search"
              value={getSelectedJobCodeKeyOptions()}
              closeMenuOnSelect={false}
            />
            }
          </div>
          }
        </>
        }
      </>
    )
  }

  const renderRuleCards = (): ReactElement | null => {
    if (!rules?.data?.length) {
      return null;
    }

    let selectJobsTitle;
    const cardBody = rules?.data?.map(rule => {
      selectJobsTitle = currentRuleSteps[rule.id] === RuleStep.SELECT_JOBS ? (
        <div>
          Select jobs to include in version <span className="create-pricing-rule__subheader">
          (If "Propagate Upload Match Priorities" is selected above, job filters in this section will not be applied.) </span>
        </div>
      ) : "Select jobs to include in version";
      return (
        <>
          <div className="card-content__header">
            <InlineEdit
              text={rule.name}
              onSave={(newValue: string): boolean => {
                const newRuleDetails = { ...rule, name: newValue };
                const successMessage = `Rule “${rule.name}” successfully renamed to “${newValue}”`;
                const errorMessage = `Rule “${rule.name}” failed to be renamed.`;
                return handleRuleUpdate(newRuleDetails, successMessage, errorMessage);
              }}
              errorMessage={`${VERSION_NAME_MINIMUM_LENGTH} character minimum and ${VERSION_NAME_MAXIMUM_LENGTH} character maximum.`}
            />
            {rules.data.length > 1 && (
              <span onClick={handleDeleteRule(rule)} title="Delete rule">
                <Icon className="icon delete-rule__icon" name="Trash" />
              </span>
            )}
          </div>
          <div className="rule__container">
            <div className="rule-details">
              <RuleStepCard
                pricingVersion={pricingVersionDetails}
                rule={rule}
                defaultGroup={defaultGroup}
                groups={groups.filter((group: { id: number }) => group.id !== defaultGroup.id)}
                jobPricingRules={jobPricingRules}
                marketElements={marketElements}
                currentRuleStep={currentRuleSteps[rule.id]}
                handleRuleUpdate={handleRuleUpdate}
                filters={filters || []}
                areFiltersLoading={areFiltersLoading}
                areFiltersLoaded={areFiltersLoaded}
              />
            </div>
            <div className="step-buttons__container">
              {currentRuleSteps[rule.id] !== RuleStep.SELECT_JOBS && (
                <Button
                  className="previous-step__button"
                  variant="secondary"
                  onClick={goToPreviousRuleStep.bind(this, rule)}
                >
                  Back
                </Button>
              )}
              {currentRuleSteps[rule.id] !== RuleStep.METHODOLOGY && (
                <Button className="next-step__button" variant="secondary" onClick={goToNextRuleStep.bind(this, rule)}>
                  {currentRuleSteps[rule.id] === RuleStep.SELECT_JOBS ? "Select Data Cut Priorities" : "Select Methodology"}
                </Button>
              )}
            </div>
          </div>
        </>
      );
    });

    return (
      <ActionableHeaderCard
        className="rule__card"
        cardTitle={selectJobsTitle}
        cardBodyClassName="create-pricing-rule__body"
      >
        {renderRuleDeleteModal()}
        {cardBody}
        <Button className="add-rule__link" variant="link" onClick={createNewRule}>
          + Add another set of jobs
        </Button>
      </ActionableHeaderCard>
    );
  };

  if (error || rulesError) {
    backToLanding();
    addAlert('Could not load global job pricing version', 'danger');
    return <div>Error loading page, redirecting.</div>;
  } else if (!pricingVersionDetails) {
    return <div>loading...</div>;
  } else {
    return (
      <div className="edit-pricing-version__container">
        <div className="header__container">
          <div className="left__section">
            <Button className="back__link" variant="link" onClick={backToLanding}>
              Back to All Versions
            </Button>
            <InlineEdit
              text={pricingVersionDetails.name}
              onSave={(newValue: string): boolean => {
                const newDetails = { ...pricingVersionDetails, name: newValue };
                const successMessage = `Version “${pricingVersionDetails.name}” successfully renamed to “${newValue}”`;
                const errorMessage = `Failed to rename version “${pricingVersionDetails.name}”.`;
                return handleVersionUpdate(newDetails, successMessage, errorMessage);
              }}
              onError={(msg: string): void => {
                const errorMessage = `Version “${pricingVersionDetails.name}” failed to be renamed. ` + msg;
                addAlert(errorMessage, 'danger', 5000);
              }}
              errorMessage={`Version name has a ${VERSION_NAME_MINIMUM_LENGTH} character minimum and a ${VERSION_NAME_MAXIMUM_LENGTH} character maximum.`}
            />
            <div className="modified-info">{`Last Modified ${shortDateString(
              new Date(pricingVersionDetails.lastModified)
            )} by ${pricingVersionDetails.lastModifiedBy}`}</div>
          </div>
          <div className="right__section">
            <div className="dropdown-group">
              <Dropdown
                text="Generate Report"
                className="generate-report__dropdown"
                dropdownItems={[
                  GENERATE_REPORT_OPTIONS.PRICING_VERSION_DETAILS,
                  GENERATE_REPORT_OPTIONS.PRICING_VERSION_SUMMARY,
                  GENERATE_REPORT_OPTIONS.PRICING_VERSION_MATCH_PRIORITIES,
                  //GENERATE_REPORT_OPTIONS.PRICING_VERSION_STATS
                ]}
                disabled={pricingVersionDetails.requiresRun}
                itemOnClick={async (option: GENERATE_REPORT_OPTIONS) => {
                  if (option === GENERATE_REPORT_OPTIONS.PRICING_VERSION_DETAILS || option === GENERATE_REPORT_OPTIONS.PRICING_VERSION_SUMMARY) {
                    const versions = await getVersions(masterAccountId);
                    setVersions(versions.data);
                    setSelectedVersionIds([pricingVersionDetails.id]);
                    setGenerateReportOption(option);
                    onGenerateReportOpen();
                  } else if (option === GENERATE_REPORT_OPTIONS.PRICING_VERSION_MATCH_PRIORITIES) {
                    await downloadVersion(id, csrfPair);
                  }
                }}
              />
            </div>
            <div className="dropdown-group">
              <Dropdown
                text="Apply to Jobs"
                className="apply-to-jobs__dropdown"
                customRender={true}
                customRenderComponent={ApplyToJobsDropdown}
                customRenderProps={{
                  dropdownItems: [
                    APPLY_VERSION_CHOICES.CREATE_NEW,
                    APPLY_VERSION_CHOICES.OVERWRITE_EXISTING,
                    APPLY_VERSION_CHOICES.OVERWRITE_ALL_AND_PRESERVE
                  ],
                  onClick: (option: APPLY_VERSION_CHOICES) => {
                    setApplyVersionChoice(option);
                    onApplyVersionOpen();
                  }
                }}
                disabled={pricingVersionDetails.requiresRun}
              />
            </div>
            <Button
              onClick={async (): Promise<void> => {
                try {
                  setIsRunTheRulesLoading(true);
                  await runTheRules(masterAccountId, id);
                  addAlert('Rules are running...', 'success', 5000);
                } catch {
                  const errorMessage = `Rules have failed to run`;
                  addAlert(errorMessage, 'danger', 5000);
                } finally {
                  setIsRunTheRulesLoading(false);
                  await queryClient.invalidateQueries(key);
                }
              }}
              disabled={isFetching || isRunTheRulesLoading || !pricingVersionDetails.requiresRun || requiresMatchFileUpload() || pricingVersionDetails.runStatus === RunStatus.RUNNING}
            >
              {(isRunTheRulesLoading || pricingVersionDetails.runStatus === RunStatus.RUNNING) ? (
                <>
                  <i
                    className="edit-pricing-version__running-rules-icon icon-spin icon-animate-spin"
                    aria-hidden="true"
                  />
                  <span>Running...</span>
                </>
              ) : (
                <span>Run The Rules</span>
              )}
            </Button>
          </div>
        </div>
        <div className="pricing-version-details__container">
          <div className="side-bar__container">
            <div className="description__label">Description</div>
            {renderDescription()}
            <div className="steps__label">Steps</div>
            <ProgressStepper steps={retrieveProgressSteps()} />
          </div>
          <div className="details__container">
            <ActionableHeaderCard
              className="match__card"
              cardTitle="Define Survey Job Matches"
              cardBodyClassName="select-job-matches__body"
            >
              <div className="card-content__header">
                What Survey job matches do you want to use for this pricing version?
              </div>
              <RadioGroup name="match-group">
                <Radio
                  label="Use Current Matches"
                  value={MatchStrategy.CURRENT}
                  checked={pricingVersionDetails.matchStrategy === MatchStrategy.CURRENT}
                  onChange={(): void => {
                    handleMatchStrategyUpdate(MatchStrategy.CURRENT);
                  }}
                />
                <div className="subtext">The matches in Market Price A Job</div>
                <Radio
                  label="Use Match Priorities"
                  value={MatchStrategy.FILE}
                  checked={pricingVersionDetails.matchStrategy === MatchStrategy.FILE}
                  onChange={(): void => {
                    handleMatchStrategyUpdate(MatchStrategy.FILE);
                  }}
                />
                <div className="subtext">
                  Your match hierarchy or decision tree by job with priorities defined by match. The system will find
                  the best available match.  Survey data cut priority levels must be set in order to propagate the matches.
                </div>
                {pricingVersionDetails.matchStrategy === MatchStrategy.FILE && (
                  <div className="match-priority-file-options">
                    <Button onClick={onUploadModalOpen} className="upload-match__link" icon="Upload" variant="icon-link">
                      Upload match priorities as csv file
                    </Button>
                    {pricingVersionDetails?.metadata?.filename &&
                      <div className="file-upload__data">
                        {`${pricingVersionDetails.metadata.filename} file uploaded on ${shortDateString(new Date(pricingVersionDetails.metadata.uploadDate))}`}
                        {!!pricingVersionDetails.metadata.user &&
                          <span>{` by ${pricingVersionDetails.metadata.user}`}</span>
                        }
                      </div>
                    }
                    {renderPropagate()}
                  </div>
                )}
              </RadioGroup>
            </ActionableHeaderCard>
            {rules && renderRuleCards()}
          </div>
        </div>
        <EditPricingVersionDetailsUploadModal
          pricingVersionId={id}
          isOpen={isUploadModalOpen}
          onClose={onUploadModalClose}
          launchDarklyFeatureFlags={launchDarklyFeatureFlags}
          onSuccess={onUploadModalSuccess}
          getUploadErrors={getUploadErrors}
          downloadUploadErrorsToExcel={downloadUploadErrorsToExcel}
        />
        {renderApplyToJobsConfirmModal()}
        {renderGenerateReportModal()}
      </div>
    );
  }
}

export default EditPricingVersionDetails;
